/**
 * Derelict reactivation, the restart kit (plan S15; spec §16.3 steps 3–5, §16.4 revert).
 *
 * - **Requirement:** per class from `reactivation.json classes` (salvaged components plus `fuel_cells` and
 *   `electronics` as `GoodId`s, economy plan M7-1). A re-restart after a revert needs `rerestartMult` of it,
 *   rounded up.
 * - **Salvaged, serviceable:** a component counts only at `salvage.json bands.serviceable` condition or better
 *   (SY-1's band: the spec's "reactor at ≥ 50% condition"), never a wrecked one.
 * - **Progress persists** per POI in `career.story.reactivations[].kit`; a delivery takes only what is still
 *   missing (the rest stays in the hold). The first delivery moves the record from `surveyed` to `kit`.
 * - **Scram** (a failed reboot): the fuel cells are lost, the components stay; the retry waits
 *   `rebootRetryOnlineDays` (the caller's countdown).
 * - **Rush-pattern bonus** (M8 MC-h4b, U14): a Rush-pattern component (`provenance: 'rush'`, D-502) that fills a
 *   missing slot also credits `rushBonus.fuelCellsPerPart` fuel cells toward the kit, never more than are still
 *   missing. A bonus only: the requirement never names a Rush part, so a kit completes without one. The credit is
 *   held as delivered fuel cells, so a scram loses it with the rest.
 *
 * The delivery rule itself (serviceable components, goods up to what is missing, the Rush bonus) is the shared,
 * record-agnostic `kits/deliver.ts` (M9 CH-5a); this file keeps the reactivation record around it.
 *
 * Career-in, Career-out; pure.
 */
import type { Career } from '../../career/types'
import type { GoodId } from '../../career/parts'
import { COMPONENT_TYPES, type ComponentType } from '../../data/m2Schemas'
import {
  deliverItem,
  kitComplete,
  rushCellCredit,
  SALVAGE_BANDS,
  type KitItem,
  type KitRequirement,
  type SalvageBands,
} from '../../kits/deliver'
import type { ReactivationConfig } from '../schemas'
import { storyOf, withStory } from '../state'
import type { FrClass, StoryReactivation } from '../types'

export { acceptsComponent, kitComplete, kitMissing, SALVAGE_BANDS, type KitRequirement, type SalvageBands } from '../../kits/deliver'

/** The kit for `frClass`; × `rerestartMult` (rounded up) for a re-restart after a revert. */
export function kitRequirement(frClass: FrClass, cfg: ReactivationConfig, rerestart = false): KitRequirement {
  const c = cfg.classes[frClass]
  const scale = (n: number): number => (rerestart ? Math.ceil(n * cfg.rerestartMult) : n)
  const components: Partial<Record<ComponentType, number>> = {}
  for (const t of COMPONENT_TYPES) {
    const n = c.components[t]
    if (n !== undefined) components[t] = scale(n)
  }
  const goods: Partial<Record<GoodId, number>> = {}
  for (const [g, n] of Object.entries(c.goods) as [GoodId, number | undefined][]) if (n !== undefined) goods[g] = scale(n)
  return { components, goods }
}

/** The fuel cells a Rush-pattern component credits to a kit that still misses `missingCells` (U14 bonus). */
export function rushCredit(provenance: 'rush' | undefined, missingCells: number, cfg: Pick<ReactivationConfig, 'rushBonus'>): number {
  return rushCellCredit(provenance, missingCells, cfg.rushBonus.fuelCellsPerPart)
}

/** One delivery to a restart kit (the shared `KitItem`; a Rush-pattern part credits `rushBonus.fuelCellsPerPart`). */
export type KitDelivery = KitItem

export type KitRefusal = 'noRecord' | 'closed' | 'wrecked' | 'notNeeded'

export type KitDeliveryResult =
  | {
      ok: true
      career: Career
      /** Units taken (a good delivery takes at most what is missing). */ taken: number
      /** Fuel cells a Rush-pattern component credited to the kit (0 otherwise). */
      credited: number
    }
  | { ok: false; reason: KitRefusal }

const OPEN_STATES: ReadonlySet<StoryReactivation['state']> = new Set(['surveyed', 'kit', 'reverted'])

/**
 * Delivers one salvaged component or a quantity of a good to the kit at `poiId` (spec §16.3 step 4). The record
 * must exist (surveyed) and still be open (surveyed, kit, or reverted for a re-restart).
 */
export function deliverToKit(
  career: Career,
  poiId: string,
  delivery: KitDelivery,
  cfg: ReactivationConfig,
  bands: SalvageBands = SALVAGE_BANDS,
): KitDeliveryResult {
  const record = storyOf(career).reactivations.find((r) => r.poiId === poiId)
  if (record === undefined || record.frClass === undefined) return { ok: false, reason: 'noRecord' }
  if (!OPEN_STATES.has(record.state)) return { ok: false, reason: 'closed' }
  const req = recordRequirement(record, cfg) ?? kitRequirement(record.frClass, cfg)
  const r = deliverItem(req, record.kit, delivery, { bands, rushFuelCellsPerPart: cfg.rushBonus.fuelCellsPerPart })
  if (!r.ok) return { ok: false, reason: r.reason }
  const { delivered: next, taken, credited } = r
  const updated: StoryReactivation = { ...record, state: 'kit', kit: next }
  if (record.state === 'reverted') updated.rerestart = true
  return { ok: true, career: replaceRecord(career, updated), taken, credited }
}

/** A failed reboot (spec §16.3 step 5): the fuel cells are lost, the components stay. */
export function scramKit(career: Career, poiId: string): Career {
  const record = storyOf(career).reactivations.find((r) => r.poiId === poiId)
  if (record?.kit === undefined) return career
  const goods: Record<string, number> = { ...record.kit.goods }
  delete goods.fuel_cells
  return replaceRecord(career, { ...record, kit: { ...record.kit, goods } })
}

/** The kit requirement of a record (a re-restart's when it reverted). */
export function recordRequirement(record: StoryReactivation, cfg: ReactivationConfig): KitRequirement | null {
  if (record.frClass === undefined) return null
  return kitRequirement(record.frClass, cfg, record.state === 'reverted' || record.rerestart === true)
}

/**
 * A reboot that held (spec §16.3 step 5): a complete kit moves the record to `lit` (awaiting registration) and
 * clears the re-restart mark. Returns null when the kit isn't complete or the record isn't in `kit`.
 */
export function markLit(career: Career, poiId: string, onlineDay: number, cfg: ReactivationConfig): Career | null {
  const record = storyOf(career).reactivations.find((r) => r.poiId === poiId)
  if (record === undefined || record.state !== 'kit') return null
  const req = recordRequirement(record, cfg)
  if (req === null || !kitComplete(req, record.kit)) return null
  const next: StoryReactivation = {
    ...record,
    state: 'lit',
    lastLitDay: onlineDay,
  }
  delete next.rerestart
  return replaceRecord(career, next)
}

/** Replaces the record with the same POI id. */
export function replaceRecord(career: Career, record: StoryReactivation): Career {
  return withStory(career, (s) => ({
    ...s,
    reactivations: s.reactivations.map((r) => (r.poiId === record.poiId ? record : r)),
  }))
}
